import React, { useState, useEffect } from 'react';
import { Typography, FormControlLabel, TextField, Button, Checkbox, CircularProgress, Box } from '@material-ui/core';
import { ActivityTypeSelect } from '@shared/components/forms/ActivityTypeSelect';
import { ActivityGroupSelect } from '@shared/components/forms/ActivityGroupSelect';
import { TimezoneSelect } from '@shared/components/forms/TimezoneSelect';
import { StartOutHourModal } from './StartOutHourModal';
import { StopOutHourModal } from './StopOutHourModal';
import { AutoStopAt8PMModal } from './AutoStopAt8PMModal';
import { useStyles } from '../styles/InputsDirectActivity.styles';
import {
  useInProgressActivity,
  useTodayActivities,
  useStartActivity,
  useStopActivity
} from '../hooks/useActivityQueries';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getUserTimezone } from '@shared/utils/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// ------------------------------------------------------------------------ //
// COMPONENT
// ------------------------------------------------------------------------ //
const InputsDirectActivity = ({ activeHourSelected }) => {
  const classes = useStyles();

  // const [activityId, setActivityId] = useState('');
  const [activityTypeId, setActivityTypeId] = useState('');
  const [activityGroupId, setActivityGroupId] = useState('');
  const [specialActivityName, setSpecialActivityName] = useState('');
  const [timezoneId, setTimezoneId] = useState('');
  const [reinforcement, setReinforcement] = useState(false);
  const [guest, setGuest] = useState(false);
  const [openStartModal, setOpenStartModal] = useState(false);
  const [openStopModal, setOpenStopModal] = useState(false);
  const [openAutoStopModal, setOpenAutoStopModal] = useState(false);
  const [hasShown8PMModal, setHasShown8PMModal] = useState(false);

  // Utilisation des hooks React Query
  const { data: inProgressActivity, isLoading: isLoadingActivity } = useInProgressActivity();
  const { refetch: refetchTodayActivities } = useTodayActivities();
  const startActivityMutation = useStartActivity();
  const stopActivityMutation = useStopActivity();

  useEffect(() => {
    // if (activeHourSelected === false && started) {
    //   setOpenStopModal(true);
    // }
  }, [activeHourSelected]);

  useEffect(() => {
    const checkFor8PM = () => {
      const userTimezone = getUserTimezone();
      const now = dayjs().tz(userTimezone);

      const currentHour = now.hour();
      const currentMinute = now.minute();

      if (currentHour === 19 && currentMinute === 59 && inProgressActivity && !hasShown8PMModal) {
        setOpenAutoStopModal(true);
        setHasShown8PMModal(true);
      }

      if (currentHour === 0 && currentMinute === 0) {
        setHasShown8PMModal(false);
      }
    };

    const interval = setInterval(checkFor8PM, 1000);

    checkFor8PM();

    return () => clearInterval(interval);
  }, [inProgressActivity, hasShown8PMModal]);

  const handleCloseStopModal = (event) => {
    setOpenStopModal(false);
  };

  const handleCloseAutoStopModal = (event) => {
    setOpenAutoStopModal(false);
  };

  const handleAutoStopActivity = async (event) => {
    setOpenAutoStopModal(false);
    await handleStopActivity(event);
  };

  const handleStartActivity = async (event) => {

    const params = {
      typeId: activityTypeId,
      groupId: activityGroupId,
      specialName: specialActivityName,
      timezoneId: timezoneId,
      isReinforcment: reinforcement ? 1 : 0,
      isGuest: guest ? 1 : 0
    };

    const cb = {
        onSuccess: () => {
          refetchTodayActivities();
          setOpenStartModal(false);
        }
    };

    startActivityMutation.mutate(
      params,
      cb
    );
  };

  const handleStopActivity = async (event) => {
    const { id } = inProgressActivity;
    stopActivityMutation.mutate(
      id,
      {
        onSuccess: () => {
          refetchTodayActivities();
          setActivityTypeId('');
          setActivityGroupId('');
          setTimezoneId('');
          setReinforcement(false);
          setGuest(false);
          setSpecialActivityName('');
          setOpenStopModal(false);

          // Afficher une notification Electron si disponible
          if (window.electron?.notification) {
            window.electron.notification.show(
              'Activité arrêtée',
              'Votre activité a été arrêtée avec succès.'
            );
          }
          
          setOpenAutoStopModal(false);
          setHasShown8PMModal(false);
        }
      }
    );
  };

  return (
    <div className={classes.column}>
      <Typography component={'span'} variant="h5">Lancer une activité en direct</Typography>

      {/* SELECT GROUP */}
      <ActivityGroupSelect
        handleActivityGroupId={(event) => {
          setActivityGroupId(event.target.value);
        }}
        activityGroupId={activityGroupId}
        disabled={isLoadingActivity || inProgressActivity !== null}
      />

      {/* SELECT TYPE */}
      <ActivityTypeSelect
        activityTypeId={activityTypeId}
        handleActivityTypeId={(event) => {
          setActivityTypeId(event.target.value);
        }}
        disabled={isLoadingActivity || inProgressActivity !== null}
      />

      {/* INPUT TEXT ACTIVITY NAME */}
      {activityTypeId === 4 && (
        <TextField
          margin="dense"
          id="activityType"
          variant="outlined"
          label="Intitulé de l'activité"
          value={specialActivityName}
          disabled={isLoadingActivity || inProgressActivity !== null}
          onChange={(event) => {
            setSpecialActivityName(event.target.value);
          }}
          fullWidth
        />
      )}

      {/* SELECT TIMEZONE */}
      <TimezoneSelect
        timezoneId={timezoneId}
        handleTimezoneId={(event) => {
          setTimezoneId(event.target.value);
        }}
        disabled={isLoadingActivity || inProgressActivity !== null}
      />

      {/* CHECKBOX RENFORT */}
      {activityTypeId === 1 && (
        <FormControlLabel
          label="Activité de renfort"
          control={
            <Checkbox
              checked={reinforcement}
              onChange={(event) => {
                setReinforcement(event.target.checked);
              }}
              disabled={isLoadingActivity || inProgressActivity !== null}
              name="reinforcement"
              color="primary"
            />
          }
        />
      )}

      {/* CHECKBOX GUEST */}
      {activityTypeId === 1 && (
        <FormControlLabel
          label="Invité"
          control={
            <Checkbox
              checked={guest}
              onChange={(event) => {
                setGuest(event.target.checked);
              }}
              disabled={isLoadingActivity || inProgressActivity !== null}
              name="guest"
              color="primary"
            />
          }
        />
      )}

      {/* SUBMIT BUTTON */}
      {isLoadingActivity ? (
        <Box display="flex" justifyContent="center" alignItems="center" className={classes.button}>
          <CircularProgress size={24} />
        </Box>
      ) : inProgressActivity ? (
        <Button
          variant="contained"
          margin="dense"
          fullWidth
          className={classes.stopButton}
          onClick={(event) => {
            setOpenStopModal(true);
          }}
          // onClick={handleStopActivity}
        >
          Arrêter l'activité
        </Button>
      ) : (
        <Button
          variant="contained"
          margin="dense"
          color="primary"
          fullWidth
          disabled={!activityGroupId || !activityTypeId || !timezoneId}
          className={classes.button}
          onClick={activeHourSelected ? handleStartActivity : (event) => {
            setOpenStartModal(true);
          }}
        >
          Lancer l'activité
        </Button>
      )}

      <StartOutHourModal
        openModal={openStartModal}
        handleCloseModal={(event) => {
          setOpenStartModal(false);
          setActivityGroupId('');
          setActivityTypeId('');
          setTimezoneId('');
          setReinforcement(false);
          setGuest(false);
          setSpecialActivityName('');
        }}
        handleStartActivity={handleStartActivity}
      />

      <StopOutHourModal
        openModal={openStopModal}
        handleCloseModal={handleCloseStopModal}
        handleStopActivity={handleStopActivity}
      />

      <AutoStopAt8PMModal
        openModal={openAutoStopModal}
        handleCloseModal={handleCloseAutoStopModal}
        handleStopActivity={handleAutoStopActivity}
        activityId={inProgressActivity?.id}
      />
    </div>
  );
};

export default InputsDirectActivity;
